import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { ethers } from "ethers";
import "./App.css";
import WalletModal from "./components/WalletModal";
import WalletScreen from "./components/WalletScreen";
import TransactionsScreen from "./components/TransactionsScreen";
import BidDistributionChart from "./components/BidDistributionChart";
import Dashboard from "./components/Dashboard";
import { useTranslation } from "./localization";

const resolveApiBaseUrl = () => {
  if (typeof window === "undefined") {
    return process.env.REACT_APP_API_URL || "http://localhost:3000/api";
  }

  const host = window.location.hostname;

  if (host === "bidarenas.com" || host === "www.bidarenas.com") {
    return "https://api.bidarenas.com/api";
  }

  if (host === "localhost" || host === "127.0.0.1") {
    return process.env.REACT_APP_API_URL || "http://localhost:3000/api";
  }

  return process.env.REACT_APP_API_URL || `${window.location.origin}/api`;
};

const API_BASE_URL = resolveApiBaseUrl();

// Debug mode - disable verbose logging in production to prevent memory leaks
const DEBUG_MODE = process.env.NODE_ENV === "development";

// Safe console wrapper that only logs in debug mode
const debugLog = (...args) => {
  if (DEBUG_MODE) {
    console.log(...args);
  }
};

const FREE_PLAY_BID_STEP = 0.1;
const REAL_PLAY_BID_STEP = 0.1;
const FREE_PLAY_STARTING_BALANCE = 10;
const FREE_PLAY_STORAGE_VERSION = "2";

const normalizeBidAmountForMode = (amount, isFreePlayMode) => {
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) {
    return isFreePlayMode ? FREE_PLAY_BID_STEP : REAL_PLAY_BID_STEP;
  }

  const step = isFreePlayMode ? FREE_PLAY_BID_STEP : REAL_PLAY_BID_STEP;
  return Math.max(step, Math.round(numericAmount / step) * step);
};

// Helper function to format currency amounts
const formatCurrency = (amount, isFreePlayMode = false) => {
  const roundedAmount = Number(amount);
  if (!Number.isFinite(roundedAmount)) {
    return "0¢";
  }

  return roundedAmount < 1
    ? `${Math.round(roundedAmount * 100)}¢`
    : `$${roundedAmount.toFixed(2)}`;
};

function App() {
  const { t } = useTranslation();
  const [currentTab, setCurrentTab] = useState("game");
  const [walletAddress, setWalletAddress] = useState("");
  const [authToken, setAuthToken] = useState(null);
  const [platformBalance, setPlatformBalance] = useState(0);
  const [withdrawableBalance, setWithdrawableBalance] = useState(0);
  const [promotionalBalance, setPromotionalBalance] = useState(0);
  const [promotionClaimed, setPromotionClaimed] = useState(false);
  const [onChainBalance, setOnChainBalance] = useState(0);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [currentGame, setCurrentGame] = useState(null);
  const [bidAmount, setBidAmount] = useState(FREE_PLAY_BID_STEP);
  const [placingBid, setPlacingBid] = useState(false);
  const [lastBidPlaced, setLastBidPlaced] = useState(null);
  const [lastBidGameId, setLastBidGameId] = useState(null);
  const [submittedBid, setSubmittedBid] = useState(null);
  const [pendingNextGameBid, setPendingNextGameBid] = useState(null); // Bid queued for next game
  // const [wonPrize, setWonPrize] = useState(null);
  // const [lostGame, setLostGame] = useState(false);
  // const [errorMessage, setErrorMessage] = useState(null);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [showHistory, _setShowHistory] = useState(false); // eslint-disable-line no-unused-vars
  const [provider, setProvider] = useState(null);
  const [lastCompletedGame, setLastCompletedGame] = useState(null);
  const [transactionRefreshTrigger, setTransactionRefreshTrigger] = useState(0);
  const [lastShownGameId, setLastShownGameId] = useState(null);
  const [walletModeOverride, setWalletModeOverride] = useState(null);
  const [walletActionRequest, setWalletActionRequest] = useState(null);
  const [walletInfoRequest, setWalletInfoRequest] = useState(null);
  const [walletInfoDismissRequest, setWalletInfoDismissRequest] =
    useState(null);
  const [isReadMeOpen, setIsReadMeOpen] = useState(false);
  const [walletAmount, setWalletAmount] = useState("1.00");
  const [selectedFont] = useState("Poppins");
  const [bodyFont] = useState("Inter");
  const [isWalletGeoRestricted, setIsWalletGeoRestricted] = useState(false);
  const [isFreePlayMode, setIsFreePlayMode] = useState(true);
  // const [freePlayFeedback, setFreePlayFeedback] = useState(null);
  const [lastFreePlayBid, setLastFreePlayBid] = useState(null);
  // const [realPlayFeedback, setRealPlayFeedback] = useState(null);
  const [lastRealPlayBid, setLastRealPlayBid] = useState(null);
  const [walletType, setWalletType] = useState(null);
  const [, setSessionExpired] = useState(false);
  const [previousLWB, setPreviousLWB] = useState(null);
  const [freePlayBalance, setFreePlayBalance] = useState(() => {
    // Version the client-only balance so pre-rollout $100 state cannot survive.
    localStorage.removeItem("freePlayBalance");
    localStorage.setItem(
      "bidArenasFreePlayBalanceVersion",
      FREE_PLAY_STORAGE_VERSION,
    );
    return FREE_PLAY_STARTING_BALANCE;
  });
  const [, setAnimatedBalance] = useState(FREE_PLAY_STARTING_BALANCE);
  const [animatedPlatformBalance, setAnimatedPlatformBalance] = useState(0);
  const [isBidPlaced, setIsBidPlaced] = useState(false);
  const [balanceAnimation, setBalanceAnimation] = useState(null);
  const [displayPotPersistent, setDisplayPotPersistent] = useState(0);
  const [winningBid, setWinningBid] = useState(null);
  const [, setFreePlayRollover] = useState(0);
  const [pendingFreePlayBid, setPendingFreePlayBid] = useState(null);
  const [lastWinGameId, setLastWinGameId] = useState(null);
  const [winningPotTotal, setWinningPotTotal] = useState(null); // Store actual pot total won
  const [finishedGameId, setFinishedGameId] = useState(null); // Store finished game ID for display
  const [lastGameBidSummary, setLastGameBidSummary] = useState(null); // Store bid distribution for chart
  const [receivedBidDistributionGameId, setReceivedBidDistributionGameId] =
    useState(null); // Tracks which game has finalized distribution available
  const [showGameHistory, setShowGameHistory] = useState(false); // Toggle between game and history view
  const [gameHistory, setGameHistory] = useState([]); // Array of last 10 completed games
  const [autoBidEnabled, setAutoBidEnabled] = useState(false); // Auto-bid same amount each game
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [prevLwb, setPrevLwb] = useState(null);
  const [animatedLwb, setAnimatedLwb] = useState(null);
  const [lwbAnimating, setLwbAnimating] = useState(false);
  const [connectedPlayers, setConnectedPlayers] = useState(0);
  const [, setPlayerCountryCodes] = useState([]);
  // const [potFadeIn, setPotFadeIn] = useState(false); // Track pot fade-in for new games (removed with game-time-card)
  const [isFirstPotUpdate, setIsFirstPotUpdate] = useState(true); // Track first pot update for immediate display
  // const [barFadeIn, setBarFadeIn] = useState(false); // Track countdown bar fade-in (removed with countdown bar)
  // const [bidAmountPulse, setBidAmountPulse] = useState(false); // Track bid amount pulse animation
  // const [showBorderGradient, setShowBorderGradient] = useState(false); // Track border gradient animation (removed with game-time-card)
  // const [borderGradientFadingOut, setBorderGradientFadingOut] = useState(false); // Track fade-out state (removed with game-time-card)

  const incrementIntervalRef = useRef(null);
  const decrementIntervalRef = useRef(null);
  // const incrementTimeoutRef = useRef(null); // Timeout before continuous increment starts
  // const decrementTimeoutRef = useRef(null); // Timeout before continuous decrement starts
  const gameCheckIntervalRef = useRef(null);
  const gameEndTimeoutRef = useRef(null);
  const bidTimeoutRef = useRef(null); // Timeout for stuck bids
  const autoBidPlacedTimerRef = useRef(null);
  const balanceAnimationRef = useRef(null); // Track free play balance animation frame
  const freePlayBidSubmissionRef = useRef({
    inFlightGameId: null,
    lastSubmittedGameId: null,
  });
  const realPlayBidSubmissionRef = useRef({
    inFlightGameId: null,
    lastSubmittedGameId: null,
  });
  const leaderboardFetchedRef = useRef(false);

  // Ref to store latest bid info for SSE handler (avoids closure staleness)
  const pendingBidRef = useRef(null); // { gameId, amount, timestamp }

  // SSE connection tracking
  const [sseConnectionStatus, setSseConnectionStatus] =
    useState("disconnected");
  const lastSSEMessageTimeRef = useRef(null); // Use ref to avoid closure issues
  const sseMonitoringIntervalRef = useRef(null);

  // Refs for SSE handler to access current values (avoid stale closures)
  const isFreePlayModeRef = useRef(isFreePlayMode);
  const lastRealPlayBidRef = useRef(lastRealPlayBid);
  const lastBidGameIdRef = useRef(lastBidGameId);
  const lastShownGameIdRef = useRef(lastShownGameId);
  const walletAddressRef = useRef(walletAddress);
  const authTokenRef = useRef(authToken);
  const pendingNextGameBidRef = useRef(pendingNextGameBid);
  const freePlayRolloverRef = useRef(0); // Track rollover to avoid stale state
  const finishedGameIdRef = useRef(null); // Track finished game immediately (avoid state lag)
  const lastRealPlayBalanceUpdateRef = useRef({
    gameId: null,
    optimistic: false,
  });

  // Keep refs synchronized with state
  useEffect(() => {
    debugLog("🔄 isFreePlayMode changed to:", isFreePlayMode);
    isFreePlayModeRef.current = isFreePlayMode;
  }, [isFreePlayMode]);

  useEffect(() => {
    setBidAmount((previousBidAmount) =>
      normalizeBidAmountForMode(previousBidAmount, isFreePlayMode),
    );
  }, [isFreePlayMode]);

  useEffect(() => {
    lastRealPlayBidRef.current = lastRealPlayBid;
  }, [lastRealPlayBid]);

  useEffect(() => {
    lastBidGameIdRef.current = lastBidGameId;
  }, [lastBidGameId]);

  useEffect(() => {
    lastShownGameIdRef.current = lastShownGameId;
  }, [lastShownGameId]);

  useEffect(() => {
    walletAddressRef.current = walletAddress;
  }, [walletAddress]);

  useEffect(() => {
    authTokenRef.current = authToken;
  }, [authToken]);

  useEffect(() => {
    let isMounted = true;

    axios
      .get(`${API_BASE_URL}/geo/restriction`)
      .then((response) => {
        if (isMounted) {
          setIsWalletGeoRestricted(response.data?.restricted === true);
        }
      })
      .catch((error) => {
        debugLog("Real-play geolocation lookup unavailable:", error.message);
        if (isMounted) {
          setIsWalletGeoRestricted(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    pendingNextGameBidRef.current = pendingNextGameBid;
  }, [pendingNextGameBid]);

  // Trigger pot fade-in when new game starts
  useEffect(() => {
    // Reset fade state when game ends
    if (!currentGame) {
      // setPotFadeIn(false);
      // setBarFadeIn(false);
      return;
    }

    // Start bar fade-in immediately
    // setBarFadeIn(true);

    return () => {};
  }, [currentGame]);

  useEffect(() => {
    return () => {
      if (autoBidPlacedTimerRef.current) {
        clearTimeout(autoBidPlacedTimerRef.current);
        autoBidPlacedTimerRef.current = null;
      }
    };
  }, []);

  // Trigger bid amount pulse animation when bid is placed
  useEffect(() => {
    if (isBidPlaced) {
      // setBidAmountPulse(true);
      // Reset after animation completes (1 pulse * 1s = 1s)
      const timer = setTimeout(() => {
        // setBidAmountPulse(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isBidPlaced]);

  // Auto-submit pending bid when new game starts
  useEffect(() => {
    if (currentGame && currentGame.id && pendingNextGameBid && !placingBid) {
      debugLog(
        "✅ New game detected - submitting queued bid:",
        pendingNextGameBid,
      );
      setPendingNextGameBid(null); // Clear pending bid
      placeBid(pendingNextGameBid); // Submit it
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGame?.id, pendingNextGameBid, placingBid]);

  // Auto-bid when new game starts (if enabled)
  useEffect(() => {
    if (
      autoBidEnabled &&
      currentGame &&
      currentGame.id &&
      !isBidPlaced &&
      !placingBid
    ) {
      debugLog("🔄 Auto-bid enabled - placing bid:", bidAmount);
      placeBid(bidAmount, { isAuto: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGame?.id, autoBidEnabled, isBidPlaced, placingBid, bidAmount]);

  // Clear bid_deducted animation when entering last 1 second
  useEffect(() => {
    if (
      currentGame &&
      (currentGame.timeLeft || 0) <= 1 &&
      balanceAnimation === "bid_deducted"
    ) {
      setBalanceAnimation(null);
    }
  }, [currentGame, balanceAnimation]);

  // Countdown timer effect - updates every second
  useEffect(() => {
    if (!currentGame || !currentGame.endTime) {
      debugLog("⏸️ Countdown timer stopped - no game or endTime", {
        currentGame: currentGame?.id,
        endTime: currentGame?.endTime,
      });
      return;
    }

    const timer = setInterval(() => {
      const now = Date.now();
      const timeLeftSeconds = Math.min(
        10, // Clamp to game duration
        Math.max(0, Math.floor((currentGame.endTime - now) / 1000)),
      );

      setCurrentGame((prev) => {
        if (!prev) return prev;
        return { ...prev, timeLeft: timeLeftSeconds };
      });

      // Stop timer when countdown reaches 0
      if (timeLeftSeconds <= 0) {
        clearInterval(timer);

        // Start gradient border fade-in as "Pot" fades out (only if not already showing)
        // setShowBorderGradient((prev) => (prev ? prev : true));

        // Keep pot value displayed until new game starts
      }
    }, 1000);

    return () => {
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGame?.id, currentGame?.endTime]);

  // Animate LWB counter when value changes
  useEffect(() => {
    if (lastCompletedGame?.free_track_lwb) {
      const currentLwb = parseFloat(lastCompletedGame.free_track_lwb);

      if (animatedLwb === null) {
        // First load - set immediately
        setAnimatedLwb(currentLwb);
        setPrevLwb(currentLwb);
      } else if (prevLwb !== currentLwb && !lwbAnimating) {
        // LWB changed - animate counter
        const startValue = animatedLwb;
        const endValue = currentLwb;
        const duration = 2000; // 2 seconds
        const startTime = Date.now();

        // Start animation
        setLwbAnimating(true);

        const animate = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);

          // Ease-out effect
          const easeProgress = 1 - Math.pow(1 - progress, 3);

          const currentValue =
            startValue + (endValue - startValue) * easeProgress;
          setAnimatedLwb(currentValue);

          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            setAnimatedLwb(endValue);
            setPrevLwb(endValue);
            setLwbAnimating(false);
          }
        };

        requestAnimationFrame(animate);
      }
    }
  }, [lastCompletedGame?.free_track_lwb, animatedLwb, prevLwb, lwbAnimating]);

  const getDisplayPot = () => {
    const isGameActive =
      currentGame && currentGame.id !== finishedGameIdRef.current;

    const activePot = isGameActive ? currentGame?.total_pot || 0 : 0;

    return activePot;
  };

  const displayPotRaw = getDisplayPot();

  useEffect(() => {
    const currentGameId = currentGame?.id;
    const isGameActive =
      currentGameId && currentGameId !== finishedGameIdRef.current;

    // Update displayed pot only from game pot state (fed by SSE/new game events)
    if (isGameActive) {
      setDisplayPotPersistent(displayPotRaw);
    }
  }, [displayPotRaw, currentGame?.id]);

  useEffect(() => {
    debugLog("💰 Display pot updated:", displayPotPersistent);
  }, [displayPotPersistent]);

  const checkWalletConnection = async () => {
    debugLog("🔍 Checking wallet connection...");
    if (window.ethereum) {
      const web3Provider = new ethers.providers.Web3Provider(window.ethereum);
      setProvider(web3Provider);

      try {
        // Check if we have a stored wallet address and auth token
        const storedAddress = localStorage.getItem("walletAddress");
        const storedToken = localStorage.getItem("authToken");
        const storedWalletType = localStorage.getItem("walletType");
        const accounts = await web3Provider.listAccounts();

        debugLog("🔍 Check results:", {
          storedAddress: storedAddress
            ? storedAddress.substring(0, 10) + "..."
            : null,
          storedToken: storedToken ? "present" : "missing",
          accountsCount: accounts.length,
        });

        if (accounts.length > 0 && storedAddress && storedToken) {
          // Wallet is connected and we have a valid session
          const currentAddress = accounts[0];
          if (storedAddress.toLowerCase() === currentAddress.toLowerCase()) {
            debugLog(
              "✅ Restoring wallet session:",
              currentAddress.substring(0, 10) + "...",
            );
            setWalletAddress(currentAddress);
            setAuthToken(storedToken);
            setSessionExpired(false);
            setIsFreePlayMode(false); // Turn off free play when wallet is restored
            debugLog("✅ Set isFreePlayMode to FALSE");
            if (storedWalletType) {
              setWalletType(storedWalletType);
            }

            // Refresh balances immediately on reconnect/session restore
            await fetchBalances(currentAddress, storedToken);
            await fetchOnChainBalance(currentAddress, web3Provider);
          } else {
            debugLog("⚠️ Address mismatch - clearing session");
            // Address changed - clear old session
            localStorage.removeItem("walletAddress");
            localStorage.removeItem("authToken");
          }
        } else {
          debugLog("⚠️ No valid session - clearing everything");
          // No valid session - clear everything
          localStorage.removeItem("walletAddress");
          localStorage.removeItem("authToken");
        }
      } catch (error) {
        console.error("Error checking wallet connection:", error);
      }
    } else {
      debugLog("⚠️ No window.ethereum found");
    }
  };

  const connectWallet = async (address, web3Provider, connectedWalletType) => {
    try {
      setProvider(web3Provider);
      setWalletType(connectedWalletType);

      // Clear any old session data
      localStorage.removeItem("authToken");
      setAuthToken(null);

      // Step 1: Request nonce from backend
      debugLog("Requesting nonce for:", address);
      const nonceResponse = await axios.post(`${API_BASE_URL}/auth/nonce`, {
        walletAddress: address,
      });
      const { nonce } = nonceResponse.data;
      debugLog("Received nonce:", nonce);

      // Step 2: Get user to sign the message
      const signer = web3Provider.getSigner();
      const message = t("app.signatureMessage", { address, nonce });

      debugLog("Requesting signature...");
      const signature = await signer.signMessage(message);
      debugLog("Signature received");

      // Step 3: Verify signature and get JWT token
      debugLog("Verifying signature...");
      const verifyResponse = await axios.post(`${API_BASE_URL}/auth/verify`, {
        walletAddress: address,
        signature,
        nonce,
      });

      const { token } = verifyResponse.data;
      debugLog("Authentication successful");

      // Step 4: Store authentication data
      setWalletAddress(address);
      setAuthToken(token);
      setSessionExpired(false);
      debugLog("🔄 Setting isFreePlayMode to FALSE (wallet connected)");
      setIsFreePlayMode(false); // Turn off free play when wallet connects
      localStorage.setItem("walletAddress", address);
      localStorage.setItem("authToken", token);
      if (connectedWalletType) {
        localStorage.setItem("walletType", connectedWalletType);
      }

      // Step 5: Fetch balances (pass values directly to avoid race condition)
      await fetchBalances(address, token);
      await fetchOnChainBalance(address, web3Provider);
    } catch (error) {
      console.error("Error connecting wallet:", error);
      console.error("Error details:", error.response?.data);

      // Clear any partial state
      setWalletAddress("");
      setAuthToken(null);
      localStorage.removeItem("walletAddress");
      localStorage.removeItem("authToken");

      if (error.response?.data?.error) {
        alert(
          t("app.connectionFailed", { error: error.response.data.error }),
        );
      } else if (error.code === 4001) {
        alert(t("app.signatureRejected"));
      } else if (error.message) {
        alert(t("app.connectionFailed", { error: error.message }));
      } else {
        alert(t("app.connectionFailedGeneric"));
      }
    }
  };

  const disconnectWallet = () => {
    setWalletAddress("");
    setAuthToken(null);
    setPlatformBalance(0);
    setWithdrawableBalance(0);
    setPromotionalBalance(0);
    setPromotionClaimed(false);
    setOnChainBalance(0);
    setProvider(null);
    setWalletType(null);
    // setFreePlayFeedback(null);
    setLastFreePlayBid(null);
    // setRealPlayFeedback(null);
    setLastRealPlayBid(null);
    debugLog("🔄 Setting isFreePlayMode to TRUE (wallet disconnected)");
    setIsFreePlayMode(true); // Turn on free play when wallet disconnects
    // Clear from localStorage
    localStorage.removeItem("walletAddress");
    localStorage.removeItem("authToken");
    localStorage.removeItem("walletType");
  };

  const fetchBalances = async (address = walletAddress, token = authToken) => {
    if (!address || !token) {
      console.warn("⚠️ fetchBalances called without address or token:", {
        address: address ? "present" : "missing",
        token: token ? "present" : "missing",
      });
      return;
    }

    try {
      debugLog("💰 Fetching balance for:", address);
      const response = await axios.get(
        `${API_BASE_URL}/wallet/balance`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      debugLog("✅ Balance response:", response.data);
      const balance = response.data.balance ?? 0;
      const parsedBalance = parseFloat(balance);
      debugLog(
        "💵 Setting platform balance to:",
        parsedBalance,
        "(from:",
        balance,
        ")",
      );
      setPlatformBalance(parsedBalance);
      setWithdrawableBalance(
        Number(response.data.withdrawableBalance) || 0,
      );
      setPromotionalBalance(Number(response.data.promotionalBalance) || 0);
      setPromotionClaimed(Boolean(response.data.promotionClaimed));
    } catch (error) {
      console.error("❌ Error fetching balances:", error);
      console.error("❌ Error response:", error.response?.data);
      console.error("❌ Error details:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        address,
      });
      // If authentication fails, clear session
      if (error.response?.status === 401) {
        console.error("🔒 Authentication failed, disconnecting wallet");
        setSessionExpired(true);
        disconnectWallet();
      }
    }
  };

  const fetchOnChainBalance = async (
    address = walletAddress,
    web3Provider = provider,
  ) => {
    if (!address || !web3Provider) return onChainBalance;

    try {
      const usdcAddress = "0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359";
      const usdcAbi = [
        "function balanceOf(address owner) view returns (uint256)",
      ];
      const usdcContract = new ethers.Contract(
        usdcAddress,
        usdcAbi,
        web3Provider,
      );
      const balance = await usdcContract.balanceOf(address);
      const balanceFormatted = parseFloat(ethers.utils.formatUnits(balance, 6));
      setOnChainBalance(balanceFormatted);
      return balanceFormatted;
    } catch (error) {
      console.error("Error fetching on-chain balance:", error);
      setOnChainBalance(0);
      return 0;
    }
  };

  // Free Play balance is session-only and resets to $10.00 on each visit.
  const updateFreePlayBalance = (newBalance, startBalance = null) => {
    const startValue = startBalance !== null ? startBalance : freePlayBalance;
    const endValue = newBalance;

    setFreePlayBalance(newBalance);

    // Dynamic duration based on win vs loss
    // Win (balance increases): 2000ms
    // Loss (balance decreases): 500ms
    const diff = endValue - startValue;
    const duration = diff > 0 ? 2000 : 500;

    const startTime = Date.now();
    let rafId = null;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-out effect
      const easeProgress = 1 - Math.pow(1 - progress, 3);

      const currentValue = startValue + (endValue - startValue) * easeProgress;
      setAnimatedBalance(currentValue);

      if (progress < 1) {
        rafId = requestAnimationFrame(animate);
      } else {
        setAnimatedBalance(endValue);
        rafId = null;
      }
    };

    rafId = requestAnimationFrame(animate);

    // Store rafId in ref so it can be cancelled if needed
    if (balanceAnimationRef.current) {
      cancelAnimationFrame(balanceAnimationRef.current);
    }
    balanceAnimationRef.current = rafId;
  };

  // Animate Real Play (wallet) balance changes
  useEffect(() => {
    if (!walletAddress || Number.isNaN(platformBalance)) {
      setAnimatedPlatformBalance(0);
      return;
    }

    // Let odometer handle interpolation from previous value to new value.
    setAnimatedPlatformBalance(platformBalance);
  }, [platformBalance, walletAddress]);

  const checkForWin = async (gameId) => {
    if (!walletAddress || !gameId || !authToken) return;

    try {
      const response = await axios.get(
        `${API_BASE_URL}/bids/my-bids/${gameId}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      );

      // Check if any of the user's bids won
      const winningBid = response.data.bids.find((bid) => bid.is_winner);
      const hasWinnerAssigned = response.data.bids.some(
        (bid) => bid.is_winner !== null,
      );

      // Only show message if this is a NEW game we haven't shown yet
      if (gameId !== lastShownGameId) {
        if (winningBid) {
          // Show win message - prize amount comes from backend
          // const prizeAmount = parseFloat(currentGame?.prize_amount || 1.0);
          // setLostGame(false);
          // setWonPrize(prizeAmount);
          setLastShownGameId(gameId);
          // setTimeout(() => {
          //   setWonPrize(null);
          // }, 10000);
        } else if (response.data.bids.length > 0 && hasWinnerAssigned) {
          // Only show loss if game has been finalized
          // setWonPrize(null);
          // setLostGame(true);
          setLastShownGameId(gameId);
          // setTimeout(() => {
          //   setLostGame(false);
          // }, 10000);
        }
      }
    } catch (error) {
      console.error("Error checking for win:", error);
      if (error.response?.status === 401) {
        setSessionExpired(true);
        disconnectWallet();
      }
    }
  };

  const checkRealPlayResult = async (gameId) => {
    debugLog("🔍 checkRealPlayResult called with gameId:", gameId);
    debugLog("🔍 State check:", {
      isFreePlayMode,
      lastRealPlayBid,
      lastBidGameId,
      lastShownGameId,
    });

    if (isFreePlayMode || !lastRealPlayBid || !gameId) {
      debugLog("❌ Early return - missing required state or in free play mode");
      return;
    }
    if (gameId === lastShownGameId) {
      debugLog("❌ Early return - already showed feedback for this game");
      return;
    }

    // Only check if this is the game we placed a bid in
    if (gameId !== lastBidGameId) {
      return;
    }

    try {
      // Fetch the completed game to get LWB
      const response = await axios.get(`${API_BASE_URL}/games/${gameId}`);
      const game = response.data.game;

      debugLog("📊 Game data received:", game);

      if (
        !game ||
        game.lowest_winning_bid === null ||
        game.lowest_winning_bid === undefined
      ) {
        debugLog("❌ No game or no lowest_winning_bid");
        return;
      }

      const realTrackLWB = parseFloat(game.lowest_winning_bid);
      const userBid = parseFloat(lastRealPlayBid);
      const difference = Math.abs(userBid - realTrackLWB);

      // Trigger appropriate animation based on result
      if (difference < 0.001) {
        const potTotal = parseFloat(game.total_pot || 0);
        setBalanceAnimation("win");

        // Update balance immediately (optimistic) and reconcile with server
        if (walletAddress) {
          const lastUpdate = lastRealPlayBalanceUpdateRef.current;
          if (lastUpdate.gameId !== gameId) {
            setPlatformBalance((prev) =>
              parseFloat((prev + potTotal).toFixed(6)),
            );
            lastRealPlayBalanceUpdateRef.current = {
              gameId,
              optimistic: true,
            };
          }
          fetchBalances(walletAddress, authToken);
        }
      } else if (userBid < realTrackLWB) {
        setBalanceAnimation("loss");
      } else {
        setBalanceAnimation("bid_too_high");
      }

      setLastShownGameId(gameId);

      // Clear bid tracking after a brief delay
      setTimeout(() => {
        setLastBidGameId(null);
        setLastRealPlayBid(null);
      }, 100);

      // Clear animation after 5 seconds
      setTimeout(() => {
        setLastBidPlaced(null);
        setTimeout(() => {
          setBalanceAnimation(null);
        }, 1000);
      }, 5000);
    } catch (error) {
      console.error("Error checking real play result:", error);
    }
  };

  const checkFreePlayResult = async (gameId) => {
    if (!isFreePlayMode || !lastFreePlayBid || !gameId) {
      return;
    }
    if (gameId === lastShownGameId) {
      return;
    }

    // Only check if this is the game we placed a bid in
    if (gameId !== lastBidGameId) {
      return;
    }

    try {
      // Fetch the completed game to get free_track_lwb
      const response = await axios.get(`${API_BASE_URL}/games/${gameId}`);
      const game = response.data.game;

      debugLog("📊 Game data received:", game);

      if (!game || !game.free_track_lwb) {
        debugLog("❌ No game or no free_track_lwb");
        return;
      }

      const freeTrackLWB = parseFloat(game.free_track_lwb);
      const userBid = parseFloat(lastFreePlayBid);
      const prizeAmount = 1.0; // Fixed $1.00 prize for Free Play

      debugLog(
        `🎯 Win Check - User Bid: ${userBid.toFixed(
          6,
        )}, Free Play LWB: ${freeTrackLWB.toFixed(6)}`,
      );

      // Check if user's bid >= Free Play LWB (wins if at or above LWB)
      const epsilon = 0.000001;
      const isWinner = userBid >= freeTrackLWB - epsilon;

      if (isWinner) {
        debugLog(`✅ WIN DETECTED! ${userBid} >= ${freeTrackLWB}`);
        const currentBalance = freePlayBalance;
        const newBalance = currentBalance + prizeAmount;
        updateFreePlayBalance(newBalance);
        debugLog(
          `💰 Free Play WIN! ${currentBalance.toFixed(
            2,
          )} + ${prizeAmount.toFixed(2)} = ${newBalance.toFixed(2)}`,
        );
      }

      setLastShownGameId(gameId);

      // Clear bid tracking after a brief delay
      setTimeout(() => {
        setLastBidGameId(null);
        setLastFreePlayBid(null);
        if (bidTimeoutRef.current) {
          clearTimeout(bidTimeoutRef.current);
          bidTimeoutRef.current = null;
        }
      }, 100);

      // Clear after 5 seconds
      setTimeout(() => {
        setLastBidPlaced(null);
      }, 5000);
    } catch (error) {
      console.error("Error checking free play result:", error);
    }
  };

  const fetchLastCompletedGame = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/games/last-completed`);
      if (response.data && response.data.game) {
        setLastCompletedGame(response.data.game);
      }
    } catch (error) {
      console.error("Error fetching last completed game:", error);
    }
  };

  const fetchCurrentGame = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/games/current`);

      if (!response.data || !response.data.game) {
        debugLog("❌ No active game from API (skipping state change)");
        return;
      }

      const gameData = response.data.game;
      const prizeAmount = parseFloat(gameData.prize_amount);
      setConnectedPlayers(gameData.connected_players || 0);

      // Prefer numeric ms timestamp from backend when available
      const endTime =
        gameData.end_time_ms || new Date(gameData.end_time).getTime();
      const now = Date.now();
      const timeLeftSeconds = Math.max(0, Math.floor((endTime - now) / 1000));

      debugLog(
        `✅ Fetched game ${gameData.id.substring(
          0,
          8,
        )}, timeLeft: ${timeLeftSeconds}s`,
      );

      // Show game if it's active (even if expired) or if no game is currently set
      if (gameData.status === "active" || !currentGame) {
        setCurrentGame((prev) => {
          const existingPot =
            prev && prev.id === gameData.id ? prev.total_pot || 0 : 0;

          return {
            id: gameData.id,
            prize_amount: prizeAmount,
            total_pot: existingPot, // Pot is updated only via SSE
            timeLeft: timeLeftSeconds,
            endTime: endTime, // Store endTime for countdown updates
            total_bids: gameData.total_bids || 0,
            status: gameData.status,
          };
        });

        if (timeLeftSeconds > 0) {
          debugLog(
            `🎮 Loaded active game in progress: ${gameData.id.substring(
              0,
              8,
            )}, ${timeLeftSeconds}s remaining`,
          );
        } else {
          debugLog(
            `⏱️ Loaded expired game ${gameData.id.substring(
              0,
              8,
            )} (awaiting new game)`,
          );
        }
      } else {
        debugLog(
          `⏭️ Skipping update for game ${gameData.id.substring(
            0,
            8,
          )} - already ended or inactive`,
        );
        return;
      }

      // Schedule immediate check when game ends (if we have a bid in this game)
      if (
        lastBidGameId === gameData.id &&
        (lastFreePlayBid || lastRealPlayBid)
      ) {
        if (timeLeftSeconds > 0) {
          // Clear any existing timeout
          if (gameEndTimeoutRef.current) {
            clearTimeout(gameEndTimeoutRef.current);
          }
          // Schedule check 2 seconds after game should end
          gameEndTimeoutRef.current = setTimeout(
            () => {
              fetchLastCompletedGame();
            },
            (timeLeftSeconds + 2) * 1000,
          );
        }
      }
    } catch (error) {
      console.error("Error fetching current game:", error);
    }
  };

  const fetchLeaderboard = useCallback(async () => {
    if (leaderboardLoading) {
      return;
    }

    setLeaderboardLoading(true);
    try {
      // Leaderboard API call intentionally disabled for now.
      setLeaderboardData([]);
    } catch (error) {
      console.error("Error fetching leaderboard:", error);
      setLeaderboardData([]);
    } finally {
      setLeaderboardLoading(false);
    }
  }, [leaderboardLoading]);

  useEffect(() => {
    if (leaderboardFetchedRef.current) {
      return;
    }

    leaderboardFetchedRef.current = true;
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  const placeBid = async (amountOverride = null, options = {}) => {
    const { isAuto = false, forceQueue = false, cancelQueue = false } = options;

    if (cancelQueue) {
      setPendingNextGameBid(null);
      return;
    }
    // Use provided amount or fall back to bidAmount state
    const rawAmount = amountOverride !== null ? amountOverride : bidAmount;
    const amount = normalizeBidAmountForMode(rawAmount, isFreePlayMode);

    // Check authentication for real bids only
    if (!isFreePlayMode && (!walletAddress || !authToken)) return;

    if (placingBid) return;

    if (
      forceQueue ||
      !currentGame ||
      !currentGame.id ||
      (finishedGameId && currentGame.id === finishedGameId) ||
      (currentGame?.timeLeft || 0) <= 0
    ) {
      // No active game - queue bid for next game
      debugLog("🔄 No active game - queueing bid for next game:", amount);
      setPendingNextGameBid(amount);
      setBidAmount(amount);
      return;
    }

    const targetGameId = currentGame.id;

    if (isFreePlayMode) {
      const { inFlightGameId, lastSubmittedGameId } =
        freePlayBidSubmissionRef.current;

      if (
        inFlightGameId === targetGameId ||
        lastSubmittedGameId === targetGameId
      ) {
        debugLog("⛔ Skipping duplicate Free Play bid for game:", targetGameId);
        return;
      }

      freePlayBidSubmissionRef.current.inFlightGameId = targetGameId;
    } else {
      const { inFlightGameId, lastSubmittedGameId } =
        realPlayBidSubmissionRef.current;

      if (
        inFlightGameId === targetGameId ||
        lastSubmittedGameId === targetGameId
      ) {
        debugLog("⛔ Skipping duplicate Real Play bid for game:", targetGameId);
        return;
      }

      realPlayBidSubmissionRef.current.inFlightGameId = targetGameId;
    }

    // Check balance only for real bids
    if (!isFreePlayMode && amount > platformBalance) {
      // setErrorMessage("Insufficient balance. Please deposit USDC first.");
      // setTimeout(() => setErrorMessage(null), 5000);
      return;
    }

    setPlacingBid(true);
    // setErrorMessage(null);

    const previousBidPlaced = isBidPlaced;
    const previousLastBidPlaced = lastBidPlaced;
    const previousLastBidGameId = lastBidGameId;
    const previousSubmittedBid = submittedBid;
    const previousLastFreePlayBid = lastFreePlayBid;
    const previousLastRealPlayBid = lastRealPlayBid;

    setLastBidPlaced(amount);
    setLastBidGameId(targetGameId);
    setSubmittedBid({ gameId: targetGameId, amount });
    lastBidGameIdRef.current = targetGameId;
    setBidAmount(amount);
    if (isAuto) {
      setIsBidPlaced(false);
      if (autoBidPlacedTimerRef.current) {
        clearTimeout(autoBidPlacedTimerRef.current);
      }
      autoBidPlacedTimerRef.current = setTimeout(() => {
        if (
          lastBidGameIdRef.current === targetGameId &&
          finishedGameIdRef.current !== targetGameId
        ) {
          setIsBidPlaced(true);
        }
        autoBidPlacedTimerRef.current = null;
      }, 50);
    } else {
      setIsBidPlaced(true);
    }

    if (isFreePlayMode) {
      setLastFreePlayBid(amount);
    } else {
      setLastRealPlayBid(amount);
    }

    try {
      const bidData = {
        amount: amount,
        gameId: targetGameId,
        is_free: isFreePlayMode,
      };

      const config = isFreePlayMode
        ? { withCredentials: true }
        : {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          };

      await axios.post(`${API_BASE_URL}/bids`, bidData, config);

      // Store bid and game ID for result checking
      if (isFreePlayMode) {
        freePlayBidSubmissionRef.current.lastSubmittedGameId = targetGameId;

        // Store in ref for SSE handler to access
        pendingBidRef.current = {
          gameId: targetGameId,
          amount: amount,
          timestamp: Date.now(),
        };

        // Set timeout to clear stuck bid after 45 seconds (game is 30s + 15s buffer)
        if (bidTimeoutRef.current) {
          clearTimeout(bidTimeoutRef.current);
        }
        bidTimeoutRef.current = setTimeout(() => {
          debugLog("⏰ Bid timeout - clearing stuck Free Play bid");
          setLastFreePlayBid(null);
          setLastBidGameId(null);
          pendingBidRef.current = null;
        }, 45000);
      } else {
        realPlayBidSubmissionRef.current.lastSubmittedGameId = targetGameId;
        debugLog(
          "💾 Storing real play bid:",
          amount,
          "for game:",
          targetGameId,
        );
      }

      // Show bid deduction animation in orange immediately after server confirms
      setBalanceAnimation("bid_deducted");
      // setBalanceDelta(-amount);

      if (!isFreePlayMode) {
        debugLog("💳 Bid placed - refreshing wallet balance");
        await fetchBalances();
      } else {
        // Deduct bid amount immediately in Free Play
        const newBalance = freePlayBalance - amount;
        debugLog(
          "💵 Balance:",
          freePlayBalance.toFixed(2),
          "→",
          newBalance.toFixed(2),
        );
        updateFreePlayBalance(newBalance);
      }

      // Fade out bid deduction animation after 2 seconds
      setTimeout(() => {
        // // setBalanceDeltaFading(true);
        setTimeout(() => {
          if (balanceAnimation === "bid_deducted") {
            // Only clear if still showing bid deduction (not replaced by win/loss)
            setBalanceAnimation(null);
          }
          // setBalanceDelta(null);
          // // setBalanceDeltaFading(false);
        }, 1000); // 1s fade transition
      }, 2000); // Show for 2 seconds before fading
      // Do not refetch current game here; rely on SSE updates to avoid countdown resets
      setTransactionRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Error placing bid:", error);
      console.error("❌ Bid error details:", {
        status: error.response?.status,
        errorMessage: error.response?.data?.error,
        fullResponse: error.response?.data,
        bidData: {
          amount: amount,
          gameId: targetGameId,
          is_free: isFreePlayMode,
        },
      });

      setIsBidPlaced(previousBidPlaced);
      setLastBidPlaced(previousLastBidPlaced);
      setLastBidGameId(previousLastBidGameId);
      setSubmittedBid(previousSubmittedBid);
      setLastFreePlayBid(previousLastFreePlayBid);
      setLastRealPlayBid(previousLastRealPlayBid);

      // const errorMsg = error.response?.data?.error || error.message;
      // setErrorMessage(errorMsg);
      // setTimeout(() => setErrorMessage(null), 5000);

      // Handle authentication errors for real bids
      if (!isFreePlayMode && error.response?.status === 401) {
        setSessionExpired(true);
        disconnectWallet();
      }
    } finally {
      if (
        isFreePlayMode &&
        freePlayBidSubmissionRef.current.inFlightGameId === targetGameId
      ) {
        freePlayBidSubmissionRef.current.inFlightGameId = null;
      } else if (
        !isFreePlayMode &&
        realPlayBidSubmissionRef.current.inFlightGameId === targetGameId
      ) {
        realPlayBidSubmissionRef.current.inFlightGameId = null;
      }
      setPlacingBid(false);
    }
  };

  const stopIncrement = () => {
    if (incrementIntervalRef.current) {
      clearInterval(incrementIntervalRef.current);
      incrementIntervalRef.current = null;
    }
  };

  const stopDecrement = () => {
    if (decrementIntervalRef.current) {
      clearInterval(decrementIntervalRef.current);
      decrementIntervalRef.current = null;
    }
  };

  // Clear old game state when new game starts
  useEffect(() => {
    if (currentGame?.id) {
      // New game detected - clear previous game state
      setLastBidPlaced(null);
      // Don't clear lastBidGameId here - need it to match completed game for feedback
      // setWonPrize(null);
      // setLostGame(false);
      // Don't clear feedback here - let it persist until timeout or new bid
    }
  }, [currentGame?.id]);

  // Countdown timer effect - updates every second
  useEffect(() => {
    if (!currentGame) {
      return;
    }

    const timer = setInterval(() => {
      setCurrentGame((prev) => {
        if (!prev || prev.timeLeft <= 0) {
          if (
            prev &&
            prev.timeLeft <= 0 &&
            lastBidPlaced &&
            lastBidGameId === prev.id
          ) {
            // Single retry after 2 seconds to catch backend finalization
            setTimeout(() => {
              // Balance now updated via SSE broadcast - no need to fetch
              checkForWin(prev.id);
              setTransactionRefreshTrigger((prevTrigger) => prevTrigger + 1);
            }, 2000);
          }

          // Do not poll API here; rely on SSE to push `new_game`

          return prev;
        }

        return { ...prev, timeLeft: prev.timeLeft - 1 };
      });
    }, 1000);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGame?.id, lastBidPlaced, lastBidGameId]);

  useEffect(() => {
    // Removed geoblock check - geolocation lookup disabled
    checkWalletConnection();
    // Fetch current game on mount in case SSE misses new_game event
    fetchCurrentGame();
    fetchLastCompletedGame(); // Get last game's LWB for display

    // Setup global SSE connection for server-wide events (new games)
    debugLog("🌍 Connecting to global SSE stream...");
    debugLog("🔗 SSE URL:", `${API_BASE_URL}/games/stream`);

    let globalEventSource;

    const setupEventSource = () => {
      const eventSource = new EventSource(`${API_BASE_URL}/games/stream`);

      eventSource.onopen = () => {
        debugLog("✅ SSE Connected - starting 20s monitor");
        setSseConnectionStatus("connected");
        lastSSEMessageTimeRef.current = Date.now();

        // Clear old monitor and start new 20s interval
        if (sseMonitoringIntervalRef.current) {
          clearInterval(sseMonitoringIntervalRef.current);
        }

        sseMonitoringIntervalRef.current = setInterval(() => {
          const timeSinceLastMessage =
            (Date.now() - lastSSEMessageTimeRef.current) / 1000;
          if (timeSinceLastMessage > 20) {
            debugLog("⚠️ SSE timeout - reconnecting...");
            setSseConnectionStatus("reconnecting");
            eventSource.close();
            clearInterval(sseMonitoringIntervalRef.current);
            setTimeout(() => {
              globalEventSource = setupEventSource();
            }, 1000);
          }
        }, 20000);
      };

      eventSource.onmessage = (event) => {
        lastSSEMessageTimeRef.current = Date.now();
        try {
          const message = JSON.parse(event.data);

          if (message.type === "new_game") {
            debugLog("🎮 New game started!", message.data);
            // Use game data directly from SSE to avoid delay
            const gameData = message.data;
            const prizeAmount = parseFloat(gameData.prize_amount);

            // Clear finished-game tracking immediately to avoid stale pot usage
            finishedGameIdRef.current = null;
            setFinishedGameId(null);

            // For a NEW game, pot starts at 0 (no bids placed yet)
            // Store server-provided starting pot (includes rollover when applicable)
            const basePot = parseFloat(gameData.total_pot || 0);

            debugLog(`🎮 New game pot - Base: ${basePot.toFixed(2)}`);

            // Use numeric timestamps from backend (already in milliseconds)
            const endTimeMs = gameData.end_time_ms;
            const now = Date.now();
            const timeLeftSeconds = Math.min(
              10, // Clamp to game duration
              Math.max(0, Math.floor((endTimeMs - now) / 1000)),
            );

            // Reset game ended state for new game
            // Don't clear winningBid/winningPotTotal - let them persist until overwritten
            setBalanceAnimation(null); // Clear win/loss messages
            if (autoBidPlacedTimerRef.current) {
              clearTimeout(autoBidPlacedTimerRef.current);
              autoBidPlacedTimerRef.current = null;
            }
            setIsBidPlaced(false); // Reset bid placed state
            setSubmittedBid(null);
            setReceivedBidDistributionGameId(null);

            // Always set game from SSE event, even if timeLeft is 0
            // The countdown timer will handle expiration
            setCurrentGame({
              id: gameData.id,
              prize_amount: prizeAmount,
              total_pot: basePot, // Store base pot only
              timeLeft: timeLeftSeconds,
              endTime: endTimeMs,
              total_bids: 0,
              status: "active",
            });
          } else if (message.type === "player_count") {
            setConnectedPlayers(message.data?.connected_players || 0);
          } else if (message.type === "player_countries") {
            const codes = (message.data?.top_countries || [])
              .map((entry) => entry.code)
              .filter(Boolean);
            setPlayerCountryCodes(codes);
          } else if (message.type === "pot_update") {
            // If this is the first pot update, show pot immediately (user joined mid-game)
            if (isFirstPotUpdate) {
              // setPotFadeIn(true);
              setIsFirstPotUpdate(false);
            }

            debugLog("📡 SSE pot_update", {
              gameId: message.gameId,
              real_pot: message.data?.real_pot,
              free_pot: message.data?.free_pot,
              total_pot: message.data?.total_pot,
              combined_pot: message.data?.combined_pot,
              has_combined_bid_summary: !!message.data?.combined_bid_summary,
            });

            // Update current pot in real-time (mode-specific)
            if (message.data) {
              setCurrentGame((prev) => {
                if (prev && prev.id === message.gameId) {
                  if (isFreePlayModeRef.current) {
                    // Free Play: use server-provided combined pot
                    if (message.data.combined_pot !== undefined) {
                      const nextPot = parseFloat(
                        message.data.combined_pot || 0,
                      );
                      return {
                        ...prev,
                        total_pot: nextPot,
                      };
                    }

                    // No combined pot provided; keep current pot
                    debugLog(
                      "⚠️ Free Play pot_update missing combined_pot; pot unchanged",
                    );
                    return prev;
                  }

                  // Real Play: use server total_pot
                  const nextPot = parseFloat(message.data.total_pot || 0);
                  return {
                    ...prev,
                    total_pot: nextPot,
                  };
                }
                debugLog(
                  "⏭️ Skipping pot update - game ID mismatch or no current game",
                );
                return prev;
              });
            }
          } else if (message.type === "game_finalized") {
            debugLog("🏆 Game finalized!", {
              ...message.data,
              bid_distribution: message.data.bid_summary,
            });

            // Store game ID for display (set ref first to avoid state lag)
            finishedGameIdRef.current = message.data.id;
            setFinishedGameId(message.data.id);

            const hasPendingBidForNextGame =
              pendingNextGameBidRef.current !== null &&
              pendingNextGameBidRef.current !== undefined;

            // Clear submitted-bid shading at finalization.
            // Pending queued bid shading remains driven by pendingNextGameBid.
            if (autoBidPlacedTimerRef.current) {
              clearTimeout(autoBidPlacedTimerRef.current);
              autoBidPlacedTimerRef.current = null;
            }
            setIsBidPlaced(false);
            setSubmittedBid(null);
            if (hasPendingBidForNextGame) {
              debugLog(
                "⏭️ Game finalized with pending queued bid; keeping pending bid shading",
              );
            }

            // Calculate Free Play LWB client-side from combined_bid_summary
            let freePlayLWB = null;
            if (message.data.combined_bid_summary) {
              const uniqueBids = message.data.combined_bid_summary
                .filter((bid) => bid.count === 1)
                .sort((a, b) => a.amount - b.amount);
              freePlayLWB = uniqueBids.length > 0 ? uniqueBids[0].amount : null;
            }

            const isCurrentlyFreePlay = isFreePlayModeRef.current;

            // Set winning bid based on current mode
            if (isCurrentlyFreePlay) {
              setWinningBid(freePlayLWB);
            } else {
              if (
                message.data.lowest_winning_bid !== null &&
                message.data.lowest_winning_bid !== undefined
              ) {
                setWinningBid(parseFloat(message.data.lowest_winning_bid));
              } else {
                setWinningBid(null);
              }
            }

            // Store bid summary for chart based on current mode
            // Free Play: combined (real + free) distribution
            // Real Play: real distribution only
            const bidSummaryToUse = isCurrentlyFreePlay
              ? message.data.combined_bid_summary
              : message.data.bid_summary;
            const realPotTotal = parseFloat(message.data.total_pot || 0);
            const combinedPotTotal = parseFloat(
              message.data.combined_pot ?? message.data.total_pot ?? 0,
            );

            let userBidAmount = null;
            let userBidWon = false;

            if (isFreePlayModeRef.current) {
              const pendingBid = pendingBidRef.current;
              const currentBid =
                pendingBid && pendingBid.gameId === message.data.id
                  ? parseFloat(pendingBid.amount)
                  : null;
              const bidToCheck = pendingFreePlayBid || currentBid;

              if (bidToCheck !== null && bidToCheck !== undefined) {
                userBidAmount = bidToCheck;
                if (freePlayLWB !== null && freePlayLWB !== undefined) {
                  userBidWon = Math.abs(bidToCheck - freePlayLWB) < 0.001;
                }
              }
            } else if (
              lastRealPlayBidRef.current &&
              lastBidGameIdRef.current === message.data.id &&
              message.data.lowest_winning_bid !== null &&
              message.data.lowest_winning_bid !== undefined
            ) {
              const realTrackLWB = parseFloat(message.data.lowest_winning_bid);
              const userBid = parseFloat(lastRealPlayBidRef.current);
              userBidAmount = userBid;
              userBidWon = Math.abs(userBid - realTrackLWB) < 0.001;
            }

            if (bidSummaryToUse) {
              setLastGameBidSummary(bidSummaryToUse);
              setReceivedBidDistributionGameId(message.data.id);

              // Add to history (newest at top, max 10 games)
              const newHistoryEntry = {
                game_id: message.data.id,
                bid_summary: message.data.bid_summary,
                combined_bid_summary: message.data.combined_bid_summary,
                lowest_winning_bid_real: message.data.lowest_winning_bid,
                free_track_lwb: freePlayLWB, // Client-calculated from combined_bid_summary
                real_pot_total: realPotTotal,
                combined_pot_total: combinedPotTotal,
                user_bid_amount: userBidAmount,
                user_bid_won: userBidWon,
                timestamp: Date.now(),
              };

              setGameHistory((prev) => {
                const withoutDuplicateGame = prev.filter(
                  (entry) => entry?.game_id !== message.data.id,
                );
                const updated = [newHistoryEntry, ...withoutDuplicateGame];
                return updated.slice(0, 10); // Keep only last 10 games
              });
            }

            // Store pot total (use appropriate pot based on mode)
            // Real Play: total_pot from server (real money only)
            // Free Play: combined_pot from server
            let potTotal;
            if (isFreePlayModeRef.current) {
              potTotal = combinedPotTotal;
            } else {
              // Real Play: use server value
              potTotal = realPotTotal;
            }
            setWinningPotTotal(potTotal);
            setDisplayPotPersistent(potTotal);

            // CLIENT-SIDE FREE PLAY WIN CALCULATION (INDEPENDENT)
            if (isFreePlayModeRef.current) {
              // Calculate pot from combined bid distribution (client-side calculation)
              const currentCombinedPot = (
                message.data.combined_bid_summary || []
              ).reduce((sum, bid) => sum + bid.amount * bid.count, 0);

              // Check if there are any unique bids in combined distribution
              const uniqueBids = (message.data.combined_bid_summary || [])
                .filter((bid) => bid.count === 1)
                .sort((a, b) => a.amount - b.amount);

              const lowestUniqueBid =
                uniqueBids.length > 0 ? uniqueBids[0].amount : null;

              // Check current bid or pending bid from previous rollover
              const pendingBid = pendingBidRef.current;
              const currentBid =
                pendingBid && pendingBid.gameId === message.data.id
                  ? parseFloat(pendingBid.amount)
                  : null;
              const bidToCheck = pendingFreePlayBid || currentBid;

              if (!lowestUniqueBid) {
                // NO FREE PLAY UNIQUE BID - POT ROLLS OVER
                const newRollover =
                  freePlayRolloverRef.current + currentCombinedPot;
                freePlayRolloverRef.current = newRollover;
                setFreePlayRollover(newRollover);
                debugLog(
                  `🔄 Free Play ROLLOVER: No unique bid. Pot ${currentCombinedPot.toFixed(2)}¢ → Total rollover now ${newRollover.toFixed(2)}¢`,
                );
                debugLog(
                  `📊 Rollover updated - Ref: ${freePlayRolloverRef.current.toFixed(2)}¢, State: ${newRollover.toFixed(2)}¢`,
                );

                // Store bid for next round if user placed one
                if (bidToCheck) {
                  setPendingFreePlayBid(bidToCheck);
                  debugLog(
                    `💾 Storing Free Play bid ${bidToCheck}¢ for next round`,
                  );
                }
              } else {
                // THERE IS A FREE PLAY WINNING BID - Check if user won (use ref for current rollover value)
                const totalPotAvailable =
                  currentCombinedPot + freePlayRolloverRef.current;

                if (
                  bidToCheck &&
                  Math.abs(bidToCheck - lowestUniqueBid) < 0.001
                ) {
                  // WIN: User's bid is the lowest unique
                  setFreePlayBalance((prevBalance) => {
                    const newBalance = prevBalance + totalPotAvailable;
                    debugLog(
                      `🎉 Free Play WIN! Bid: ${bidToCheck}¢, Prize: ${totalPotAvailable.toFixed(2)}¢ (Pot: ${currentCombinedPot.toFixed(2)}¢ + Rollover: ${freePlayRolloverRef.current.toFixed(2)}¢)`,
                    );
                    debugLog(
                      "💵 Balance:",
                      prevBalance.toFixed(2),
                      "→",
                      newBalance.toFixed(2),
                    );

                    // Trigger animation
                    updateFreePlayBalance(newBalance, prevBalance);
                    return newBalance;
                  });

                  setWinningPotTotal(totalPotAvailable);
                  setBalanceAnimation("win");
                  setLastWinGameId(message.data.id);
                  freePlayRolloverRef.current = 0; // Clear rollover ref
                  setFreePlayRollover(0); // Clear rollover state
                  setPendingFreePlayBid(null); // Clear pending bid
                } else {
                  // Someone else won Free Play or user didn't bid
                  // Clear rollover since someone won this round
                  if (bidToCheck) {
                    debugLog(
                      `❌ Free Play LOSS - Bid: ${bidToCheck}¢, Winning bid was: ${lowestUniqueBid}¢`,
                    );
                  } else {
                    debugLog(
                      `ℹ️ Free Play: Someone won (${lowestUniqueBid}¢), rollover cleared`,
                    );
                  }
                  freePlayRolloverRef.current = 0; // Clear rollover ref
                  setFreePlayRollover(0); // Clear rollover state (someone else won)
                  setPendingFreePlayBid(null); // Clear pending bid
                }
              }

              // Clear timeout and bid tracking
              if (bidTimeoutRef.current) {
                clearTimeout(bidTimeoutRef.current);
                bidTimeoutRef.current = null;
              }

              pendingBidRef.current = null;
              setLastBidGameId(null);
              setLastFreePlayBid(null);
              setLastShownGameId(message.data.id);

              // Clear feedback after 5 seconds
              setTimeout(() => {
                setLastBidPlaced(null);
                setBalanceAnimation(null);
              }, 5000);
            }

            // Check for Real Play bid (use refs to avoid stale closures)
            if (
              !isFreePlayModeRef.current &&
              lastRealPlayBidRef.current &&
              lastBidGameIdRef.current === message.data.id &&
              message.data.lowest_winning_bid !== null &&
              message.data.lowest_winning_bid !== undefined &&
              lastShownGameIdRef.current !== message.data.id
            ) {
              const realTrackLWB = parseFloat(message.data.lowest_winning_bid);
              const userBid = parseFloat(lastRealPlayBidRef.current);
              const difference = Math.abs(userBid - realTrackLWB);

              debugLog(
                "🎯 Real Play Win Check (from SSE) - User Bid:",
                userBid.toFixed(6),
                "Real Play LWB:",
                realTrackLWB.toFixed(6),
              );

              if (difference < 0.001) {
                const potTotal = parseFloat(message.data.total_pot || 0);
                debugLog("✅ WIN! Pot:", potTotal.toFixed(2));
                setWinningPotTotal(potTotal);
                setBalanceAnimation("win");
                setLastWinGameId(message.data.id);

                // Update balance immediately when win message shows
                const sseBalances = message.data.participant_balances;
                const currentWalletAddress = walletAddressRef.current;
                const sseUserBalance =
                  sseBalances && currentWalletAddress
                    ? sseBalances[currentWalletAddress.toLowerCase()]
                    : undefined;

                if (currentWalletAddress) {
                  let usedSseBalance = false;
                  setPlatformBalance((prev) => {
                    if (
                      typeof sseUserBalance === "number" &&
                      sseUserBalance > prev
                    ) {
                      usedSseBalance = true;
                      return sseUserBalance;
                    }
                    return parseFloat((prev + potTotal).toFixed(6));
                  });
                  lastRealPlayBalanceUpdateRef.current = {
                    gameId: message.data.id,
                    optimistic: !usedSseBalance,
                  };
                }
              } else if (userBid < realTrackLWB) {
                debugLog("❌ LOSS - bid too low");
              } else {
                debugLog("❌ LOSS - bid too high");
              }

              setLastShownGameId(message.data.id);

              // Clear bid tracking after a brief delay
              setTimeout(() => {
                setLastBidGameId(null);
                setLastRealPlayBid(null);
              }, 100);

              // Clear animation after 5 seconds
              setTimeout(() => {
                setLastBidPlaced(null);
                setTimeout(() => {
                  setBalanceAnimation(null);
                }, 1000);
              }, 5000);
            }

            // Update last completed game (PUSH-only, no fetching)
            setLastCompletedGame({
              id: message.data.id,
              lowest_winning_bid: message.data.lowest_winning_bid,
              free_track_lwb: freePlayLWB, // Client-calculated from combined_bid_summary
              free_track_lwb_diff: null, // No longer tracking diff
              num_winners: message.data.num_winners,
            });

            // Update Real Play balance if included in broadcast (for all participants)
            if (
              message.data.participant_balances &&
              walletAddressRef.current &&
              !isFreePlayModeRef.current
            ) {
              const userBalance =
                message.data.participant_balances[
                  walletAddressRef.current.toLowerCase()
                ];
              if (userBalance !== undefined) {
                const lastUpdate = lastRealPlayBalanceUpdateRef.current;
                if (
                  lastUpdate.gameId !== message.data.id ||
                  lastUpdate.optimistic
                ) {
                  setPlatformBalance((prev) => {
                    debugLog(
                      `💰 Balance updated from SSE: $${prev.toFixed(
                        2,
                      )} → $${userBalance.toFixed(2)}`,
                    );
                    return userBalance;
                  });
                  lastRealPlayBalanceUpdateRef.current = {
                    gameId: message.data.id,
                    optimistic: false,
                  };
                }
              }
            }

            if (
              message.data.participant_withdrawable_balances &&
              walletAddressRef.current &&
              !isFreePlayModeRef.current
            ) {
              const updatedWithdrawableBalance =
                message.data.participant_withdrawable_balances[
                  walletAddressRef.current.toLowerCase()
                ];
              if (updatedWithdrawableBalance !== undefined) {
                setWithdrawableBalance(updatedWithdrawableBalance);
                const updatedTotalBalance =
                  message.data.participant_balances?.[
                    walletAddressRef.current.toLowerCase()
                  ];
                if (updatedTotalBalance !== undefined) {
                  setPromotionalBalance(
                    Math.max(
                      0,
                      Number(
                        (
                          updatedTotalBalance - updatedWithdrawableBalance
                        ).toFixed(6),
                      ),
                    ),
                  );
                }
              }
            }

            // Fallback: if balance not included in SSE, fetch it directly
            if (
              walletAddressRef.current &&
              !isFreePlayModeRef.current &&
              (!message.data.participant_balances ||
                message.data.participant_balances[
                  walletAddressRef.current.toLowerCase()
                ] === undefined)
            ) {
              fetchBalances(walletAddressRef.current, authTokenRef.current);
            }

            // No need to fetch balance - updated via SSE broadcast
            // Do not refetch current game on finalization; SSE controls game state
            setTransactionRefreshTrigger((prev) => prev + 1);
          }
        } catch (error) {
          console.error("Error parsing global SSE message:", error);
        }
      };

      eventSource.onerror = (error) => {
        console.error("❌ SSE connection error - will reconnect");
        setSseConnectionStatus("reconnecting");
        if (sseMonitoringIntervalRef.current) {
          clearInterval(sseMonitoringIntervalRef.current);
        }
        eventSource.close();
        // Auto-reconnect after 1 second
        setTimeout(() => {
          globalEventSource = setupEventSource();
        }, 1000);
      };

      return eventSource;
    };

    // Initial setup
    globalEventSource = setupEventSource();

    // Refresh game data every 30 seconds (fallback for SSE failure)
    gameCheckIntervalRef.current = setInterval(() => {
      fetchCurrentGame(false);
      // Removed fetchLastCompletedGame() - rely on SSE push only
    }, 30000);

    return () => {
      if (globalEventSource) {
        globalEventSource.close();
        debugLog("🌍 Global SSE connection closed");
      }
      // Clear SSE monitoring interval
      if (sseMonitoringIntervalRef.current) {
        clearInterval(sseMonitoringIntervalRef.current);
      }
      if (gameCheckIntervalRef.current) {
        clearInterval(gameCheckIntervalRef.current);
      }
      if (gameEndTimeoutRef.current) {
        clearTimeout(gameEndTimeoutRef.current);
      }
      stopIncrement();
      stopDecrement();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check result when last completed game changes
  useEffect(() => {
    if (lastCompletedGame?.id) {
      if (isFreePlayMode && lastFreePlayBid) {
        checkFreePlayResult(lastCompletedGame.id);
      } else if (!isFreePlayMode && lastRealPlayBid) {
        checkRealPlayResult(lastCompletedGame.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    lastCompletedGame?.id,
    isFreePlayMode,
    lastBidGameId,
    lastFreePlayBid,
    lastRealPlayBid,
  ]);

  // Track LWB changes and show animation
  useEffect(() => {
    if (!lastCompletedGame) return;

    const currentLWB = isFreePlayModeRef.current
      ? lastCompletedGame.free_track_lwb
      : lastCompletedGame.lowest_winning_bid;

    // Skip if no current LWB value
    if (!currentLWB) return;

    // Use backend-provided difference for Free Play mode
    if (
      isFreePlayModeRef.current &&
      lastCompletedGame.free_track_lwb_diff !== undefined
    ) {
      setPreviousLWB(currentLWB);
      return;
    }

    // For Real Play mode, calculate locally (no backend diff provided)
    if (previousLWB === null) {
      // First LWB - just store it, no difference to show
      setPreviousLWB(currentLWB);
    } else if (parseFloat(currentLWB) !== parseFloat(previousLWB)) {
      // Update to new LWB value
      setPreviousLWB(currentLWB);
    }
    // If currentLWB === previousLWB, do nothing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    lastCompletedGame?.lowest_winning_bid,
    lastCompletedGame?.free_track_lwb,
    lastCompletedGame?.free_track_lwb_diff,
    isFreePlayMode,
  ]);

  useEffect(() => {
    if (!walletAddress) return;

    // Fetch balances immediately on wallet connection
    fetchBalances();
    if (currentTab === "wallet") {
      fetchOnChainBalance();
    }

    // No polling - balances update on events only (bid, deposit, withdrawal, prize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletAddress]);

  // Fetch on-chain balance when switching to wallet tab
  useEffect(() => {
    if (currentTab === "wallet" && walletAddress && provider) {
      fetchOnChainBalance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab, walletAddress, provider]);

  // Removed geoblock display - geolocation lookup disabled

  const didCurrentUserWinRealPlay = Boolean(
    walletAddress &&
    !isFreePlayMode &&
    finishedGameId &&
    currentGame?.id === finishedGameId &&
    winningBid !== null &&
    winningBid !== undefined &&
    lastBidPlaced !== null &&
    lastBidPlaced !== undefined &&
    Math.abs(parseFloat(lastBidPlaced) - parseFloat(winningBid)) < 0.001,
  );

  return (
    <div
      className="app"
      style={{
        fontFamily:
          bodyFont === "system-ui"
            ? '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            : `'${bodyFont}', sans-serif`,
      }}
    >
      <header className="header" style={{ display: "none" }}>
        <h1
          className="logo"
          style={{ fontFamily: `'${selectedFont}', sans-serif`, margin: 0 }}
        >
          Bid Arenas{" "}
          {!walletAddress && <span className="free-play-title">Free Play</span>}
        </h1>
        <div className="header-buttons">
          <button
            className="header-nav-button"
            onClick={() => {
              if (currentTab === "wallet") {
                setCurrentTab("game");
                window.scrollTo({ top: 0, behavior: "smooth" });
              } else {
                setCurrentTab("wallet");
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
          >
            {currentTab === "wallet"
              ? "Play"
              : walletAddress
                ? "Wallet"
                : "Connect"}
          </button>
          <button
            className="header-nav-button"
            onClick={() => {
              setCurrentTab("dashboard");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            Dashboard
          </button>
        </div>
      </header>

      <main className="main-content">
        {(currentTab === "game" || currentTab === "wallet") && (
          <div className="game-screen">
            {/* Bid Distribution Chart - shown for both Real and Free Play */}
            <div className="game-primary-panel">
              <BidDistributionChart
                bidSummary={lastGameBidSummary}
                hasCurrentGameBidDistribution={
                  Boolean(currentGame?.id) &&
                  receivedBidDistributionGameId === currentGame.id
                }
                lowestWinningBid={
                  isFreePlayMode
                    ? lastCompletedGame?.free_track_lwb
                    : lastCompletedGame?.lowest_winning_bid
                }
                potTotal={displayPotPersistent}
                currentBidAmount={bidAmount}
                onBidAmountChange={setBidAmount}
                isNewGame={
                  Boolean(currentGame?.id) && currentGame.id !== finishedGameId
                }
                isBidPlaced={isBidPlaced}
                lastBidPlaced={lastBidPlaced}
                lastBidGameId={lastBidGameId}
                submittedBid={submittedBid}
                pendingNextGameBid={pendingNextGameBid}
                currentGame={currentGame}
                connectedPlayers={connectedPlayers}
                onBidSubmit={placeBid}
                balance={
                  walletAddress ? animatedPlatformBalance : freePlayBalance
                }
                balanceAnimation={balanceAnimation}
                walletAddress={walletAddress}
                walletType={walletType}
                displayGameId={currentGame?.id || finishedGameId}
                onWalletConnectClick={() => {
                  if (isWalletGeoRestricted) {
                    setWalletInfoRequest({
                      type: "restriction",
                      requestId: Date.now(),
                    });
                  } else {
                    setShowWalletModal(true);
                  }
                }}
                onWalletDisconnectClick={disconnectWallet}
                walletAmount={walletAmount}
                onWalletAmountChange={setWalletAmount}
                walletMode={
                  walletModeOverride ||
                  (platformBalance >= 1 ? "withdraw" : "deposit")
                }
                walletOnChainBalance={onChainBalance}
                walletPlatformBalance={platformBalance}
                onBalanceClick={() => {
                  if (currentTab === "wallet") {
                    setCurrentTab("game");
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  } else if (walletAddress) {
                    setCurrentTab("wallet");
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  } else if (
                    currentTab === "wallet" &&
                    isWalletGeoRestricted
                  ) {
                    setWalletInfoRequest({
                      type: "restriction",
                      requestId: Date.now(),
                    });
                  } else {
                    setShowWalletModal(true);
                  }
                }}
                onWalletToggle={() => {
                  if (currentTab === "wallet") {
                    setCurrentTab("game");
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  } else {
                    setCurrentTab("wallet");
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                }}
                timeRemaining={currentGame?.timeLeft || 0}
                messageContent={
                  <span
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "rgb(100, 100, 100)",
                      transition: "color 0.5s ease",
                    }}
                  >
                    {sseConnectionStatus === "disconnected" ||
                    sseConnectionStatus === "reconnecting" ? (
                      t("game.reconnecting")
                    ) : balanceAnimation === "win" ||
                      didCurrentUserWinRealPlay ? (
                      t("game.youWon", {
                        amount: formatCurrency(
                          winningPotTotal || 0,
                          isFreePlayMode,
                        ),
                      })
                    ) : balanceAnimation === "bid_deducted" ? (
                      t("game.bidPlaced", {
                        amount: formatCurrency(bidAmount, isFreePlayMode),
                      })
                    ) : (currentGame?.timeLeft || 0) <= 1 &&
                      currentGame &&
                      currentGame.id !== finishedGameId &&
                      !autoBidEnabled &&
                      !isBidPlaced ? (
                      t("game.lowestWins")
                    ) : finishedGameId &&
                      currentGame?.id === finishedGameId &&
                      winningBid === null ? (
                      t("game.noUniqueRollover")
                    ) : balanceAnimation !== "win" &&
                      lastWinGameId !== finishedGameId &&
                      winningBid !== null &&
                      winningPotTotal !== null &&
                      finishedGameId &&
                      currentGame?.id === finishedGameId ? (
                      t("game.potWonByBid", {
                        pot: formatCurrency(
                          winningPotTotal,
                          isFreePlayMode,
                        ),
                        bid: formatCurrency(winningBid, isFreePlayMode),
                      })
                    ) : autoBidEnabled || pendingNextGameBid ? (
                      ""
                    ) : (
                      t("game.lowestWins")
                    )}
                  </span>
                }
                showGameHistory={showGameHistory}
                setShowGameHistory={setShowGameHistory}
                gameHistory={gameHistory}
                isFreePlayMode={isFreePlayMode}
                autoBidEnabled={autoBidEnabled}
                setAutoBidEnabled={setAutoBidEnabled}
                setBidAmount={setBidAmount}
                onWalletDepositClick={() => {
                  setWalletModeOverride("deposit");
                  setWalletActionRequest({
                    type: "deposit",
                    requestId: Date.now(),
                  });
                }}
                onWalletWithdrawClick={() => {
                  setWalletModeOverride("withdraw");
                  setWalletActionRequest({
                    type: "withdraw",
                    requestId: Date.now(),
                  });
                }}
                onWalletDepositsInfoClick={() =>
                  setWalletInfoRequest({
                    type: "deposit",
                    requestId: Date.now(),
                  })
                }
                onWalletWithdrawalsInfoClick={() =>
                  setWalletInfoRequest({
                    type: "withdraw",
                    requestId: Date.now(),
                  })
                }
                onWalletSecurityInfoClick={() =>
                  setWalletInfoRequest({
                    type: "security",
                    requestId: Date.now(),
                  })
                }
                onWalletReadMeCloseClick={() =>
                  setWalletInfoDismissRequest({ requestId: Date.now() })
                }
                isReadMeOpen={isReadMeOpen}
                leaderboardData={leaderboardData}
                leaderboardLoading={leaderboardLoading}
                showWalletView={currentTab === "wallet"}
                isWalletView={currentTab === "wallet"}
                walletContent={
                  <div className="wallet-area">
                    <WalletScreen
                      walletAddress={walletAddress}
                      authToken={authToken}
                      onChainBalance={onChainBalance}
                      platformBalance={platformBalance}
                      withdrawableBalance={withdrawableBalance}
                      promotionalBalance={promotionalBalance}
                      promotionClaimed={promotionClaimed}
                      provider={provider}
                      amount={walletAmount}
                      onAmountChange={setWalletAmount}
                      externalMode={walletModeOverride}
                      actionRequest={walletActionRequest}
                      infoRequest={walletInfoRequest}
                      dismissInfoRequest={walletInfoDismissRequest}
                      onActionHandled={(requestId) => {
                        setWalletActionRequest((current) =>
                          current && current.requestId === requestId
                            ? null
                            : current,
                        );
                      }}
                      onInfoHandled={(requestId) => {
                        setWalletInfoRequest((current) =>
                          current && current.requestId === requestId
                            ? null
                            : current,
                        );
                      }}
                      onInfoVisibilityChange={setIsReadMeOpen}
                      geoRestricted={isWalletGeoRestricted}
                      onDismissInfoHandled={(requestId) => {
                        setWalletInfoDismissRequest((current) =>
                          current && current.requestId === requestId
                            ? null
                            : current,
                        );
                      }}
                      onConnectWallet={() => setShowWalletModal(true)}
                      onDisconnectWallet={disconnectWallet}
                      onBackToGame={() => {
                        setCurrentTab("game");
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                      onBalanceUpdate={async () => {
                        await Promise.all([
                          fetchBalances(),
                          fetchOnChainBalance(),
                        ]);
                        setTransactionRefreshTrigger((prev) => {
                          debugLog(
                            "Incrementing transactionRefreshTrigger after deposit/withdrawal:",
                            prev + 1,
                          );
                          return prev + 1;
                        });
                      }}
                      onRefreshOnChainBalance={fetchOnChainBalance}
                    />
                  </div>
                }
              />
            </div>

            {currentTab === "game" && walletAddress && showHistory && (
              <div className="how-to-play-section">
                <div className="how-to-play-content">
                  <TransactionsScreen
                    walletAddress={walletAddress}
                    authToken={authToken}
                    inline={true}
                    refreshTrigger={transactionRefreshTrigger}
                  />
                </div>
              </div>
            )}

            {currentTab === "game" && !walletAddress && (
              <div style={{ padding: "16px", display: "none" }}>
                <button
                  className="wallet-connect-button-large"
                  onClick={() => setShowWalletModal(true)}
                  style={{ width: "100%" }}
                >
                  Connect
                </button>
              </div>
            )}
          </div>
        )}

        {currentTab === "dashboard" && <Dashboard />}
      </main>

      {showWalletModal && (
        <WalletModal
          walletAddress={walletAddress}
          platformBalance={platformBalance}
          onChainBalance={onChainBalance}
          onConnect={connectWallet}
          onDisconnect={disconnectWallet}
          onClose={() => setShowWalletModal(false)}
        />
      )}

      {showHowToPlay && (
        <div className="modal-overlay" onClick={() => setShowHowToPlay(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{t("how.title")}</h3>
              <button
                className="modal-close"
                onClick={() => setShowHowToPlay(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="how-to-play-step">
                <span className="step-text">
                  {t("how.step1")}
                </span>
              </div>
              <div className="how-to-play-step">
                <span className="step-text">
                  {t("how.step2")}
                </span>
              </div>
              <div className="how-to-play-step">
                <span className="step-text">
                  {t("how.step3")}
                </span>
              </div>
              <div className="how-to-play-step">
                <span className="step-text">
                  {t("how.step4")}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
